/**
 * BrandingProvider - the ONE reader of `GET /api/v1/branding`.
 *
 * Every SPA learns the installation's brand and legal footer from a single
 * request per page load, made here, and every consumer reads it from context
 * rather than fetching again (SPEC-FRONTEND-RULE-077 applied to a resource with
 * exactly one owner). No other module may fetch this endpoint.
 *
 * IT IS A PLAIN `fetch` ON PURPOSE, AND THE REASON IS STRUCTURAL. UI-004 puts
 * this provider ABOVE `VerisaThemeProvider` in all three SPA roots; in the admin
 * SPA `QueryClientProvider` lives INSIDE `apps/admin/App.tsx`, below where this
 * provider must mount, so a TanStack query here would have no client. The
 * shared axios instance is equally wrong: it attaches an Authorization header
 * and a tenant header, and this route is `principal: public` and renders on the
 * pre-auth login page, where sending either is both pointless and a way to turn
 * a public read into a 401. Hoisting `QueryClientProvider` was the rejected
 * alternative (audit P0-C).
 *
 * THE FAIL POSTURE IS SPLIT, AND THE SPLIT IS THE POINT (RULE-001, D-BR-15).
 * The cosmetic BRAND fails OPEN and silently: `DefaultBranding` applies and
 * nobody is told. The LEGAL FOOTER fails VISIBLE: `state` becomes
 * `'unavailable'` and the footer slot renders one translated line instead of
 * disappearing. A provider that resolved a failed read to "no footer" would
 * type-check perfectly and violate RULE-001, which is why the tests assert the
 * `state` VALUE rather than the absence of a crash.
 *
 * RENDER IS NEVER BLOCKED (SCENARIO-004, SCENARIO-012). Children mount on the
 * first frame with `DefaultBranding`; the fetched record arrives later through
 * context. There is no loading gate and no suspense boundary - `'loading'` is a
 * STATE A CONSUMER READS, never a spinner this provider imposes.
 *
 * THERE ARE THREE STATES, AND THE THIRD IS THE ONE THAT WAS MISSING (D-BR-17,
 * RULE-001 as amended). `'loading'` before the first response, `'ok'` on a 200
 * whose body shape-checks whole, `'unavailable'` on a timeout, abort, non-200,
 * network error or malformed body. Two states forced a choice between two
 * wrongs: starting at `'unavailable'` painted the fail-visible legal line on
 * EVERY login page for the length of the read, and starting at `'ok'` claimed a
 * record nobody had read. `'loading'` is not a silent third state - a mount maps
 * it deliberately (to `AppFooter`'s `'hidden'`), and the 3 s AbortController
 * bound below guarantees it is never permanent, so no surface can sit in it and
 * quietly never show the legal notice.
 *
 * ONE FURTHER READ, FOOTER ONLY, AND ONLY WHEN AUTHENTICATED (RULE-019 tier (2),
 * RULE-001 as amended by D-BR-28, SCENARIO-031). The first read cannot carry a
 * token - none exists yet: the access token lives only in AuthContext's memory
 * and the api/client getter returns null until the auth module registers one.
 * So once a token IS registered (learned through `subscribeAccessToken`, plus one
 * check at mount for a getter registered before this effect ran) and the first
 * read has settled `'ok'`, the provider reads the SAME route ONCE more with
 * `Authorization: Bearer` and applies ONLY its `legal_footer`. The brand half of
 * that response - product name, logo, favicon, colours, welcome text - is
 * DISCARDED: the page never re-themes under the user, which is the property
 * D-BR-25 protected. No timer, no re-read on navigation, focus or token refresh;
 * an unauthenticated load makes no second read; a failed second read leaves the
 * first read's footer standing and never shows the unavailable line.
 *
 * A LOGOUT PUTS THE INSTALLATION FOOTER BACK, WITHOUT A READ (CHG-001B). A forced
 * logout (session expiry) renders the login screen in place with no reload, so
 * the provider stays subscribed until unmount and, when the token goes to null
 * after the second read, restores the FIRST read's footer from memory - never
 * leaving tenant A's legal text on a login screen the next person reads. A later
 * login in the same page load does NOT read again (that would be a second
 * further read); it keeps the installation footer until the next load.
 *
 * IT RUNS INDEPENDENTLY OF THE RELEASE-TOGGLE READ (SCENARIO-012). Neither
 * bootstrap awaits the other; this one is bounded at 3 s with no retry, no poll
 * and no interval.
 *
 * THE BRAND HALF APPLIES ONLY WHAT WAS ACTUALLY READ (RULE-007, RULE-008,
 * TASK-PB-BRAND-APPLY). On `'ok'` - and only then - the provider writes
 * `document.title`, the existing favicon `<link>` href, and the pre-paint cache.
 * On `'loading'` and `'unavailable'` it writes NOTHING: whatever painted stays,
 * which is the built-in brand of the HTML entry on a first visit and the CACHED
 * brand that `web/public/theme-boot.js` applied on a repeat visit. Writing the
 * built-in brand over a cached one when the read fails would type-check, fail
 * open in the letter of RULE-001 and still flash the vendor's title and favicon
 * onto a branded installation - the exact defect RULE-008 exists to prevent.
 *
 * THE CACHE IS A TRUST BOUNDARY, AND THIS SIDE IS THE WRITER. Exactly four keys
 * ({@link BRANDING_CACHE_KEY}: `v`, `product_name`, `favicon_url`, `primary`) are
 * written, only after a successful read, inside a try/catch (a private window
 * throws on `setItem`). No footer HTML, no secondary colour, no raw response -
 * `theme-boot.js` applies its contents before any React guard exists, so the less
 * it carries the less can be tampered with. The reader VALIDATES; the two files
 * are one contract with no module edge between them, held in lockstep by
 * web/src/theme/__tests__/themeBoot.test.ts.
 *
 * THE CONTRAST VERDICT IS STATE, NOT A CONSOLE LINE. `brandContrast` reports, per
 * channel, the light ratio RULE-013 refuses on at save time and the dark ratio
 * SPEC-FRONTEND-RULE-084 gates on at apply time (D-BR-18), for the Brand card to
 * render as a RULE-042 degraded state. Nothing on this path writes to the
 * console: RULE-007 names the console warning as the defect RULE-084 retired.
 *
 * @module branding/BrandingProvider
 * @see SPEC-PLATFORM-BRANDING-UI-004
 * @see SPEC-PLATFORM-BRANDING-RULE-001 (D-BR-15 fail posture; D-BR-17 three states)
 * @see SPEC-PLATFORM-BRANDING-RULE-007 (brand colours as literal palette values)
 * @see SPEC-PLATFORM-BRANDING-RULE-008 (the validated pre-paint cache)
 * @see SPEC-PLATFORM-BRANDING-RULE-013 as amended by D-BR-18
 * @see SPEC-PLATFORM-BRANDING-RULE-019 (tier (2): the one footer-only read after login, D-BR-28)
 * @see SPEC-PLATFORM-BRANDING-CONTRACT-001
 * @see SPEC-FRONTEND-RULE-077 (one fetcher per cache key)
 * @see SPEC-FRONTEND-RULE-081 / RULE-084
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  DefaultBranding,
  type Branding,
  type BrandingColors,
  type LegalFooterLinks,
  type LegalFooterValues,
  type LocalizedText,
} from './defaults';
import type { BrandPalette } from '../theme/themes';
// The one contrast formula in the tree, shared with the Brand card and mirrored
// by internal/repository/branding_contrast.go on the server.
import { getContrastRatio, meetsAA } from '../utils/wcag';
// The token seam the auth module registers into (RULE-019 tier (2)). NOT
// AuthContext: this provider mounts ABOVE the auth provider, so it cannot read
// that context, and a login never re-renders it.
import { getAccessToken, subscribeAccessToken } from '../api/client';
import {
  actAsHeaders,
  getImpersonationDiscoveryState,
  subscribeImpersonationDiscovery,
} from '../api/impersonation';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Base URL for the public branding read. Mirrors the axios client's resolution
 * (Vite env or origin-relative `/api/v1`) exactly as the release-features
 * bootstrap does (`web/src/feature-gate/releaseFeatures.ts`), but is fetched
 * with a bare `fetch` so no interceptor attaches a credential to a public route.
 */
const API_BASE_URL: string =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';

/** The CONTRACT-001 path, relative to the base. */
const BRANDING_PATH = '/branding';

/**
 * The RULE-001 bound. A hung gateway costs the page 3 s of footer, never its
 * render. Not configurable: a longer value would be a different rule.
 */
export const BRANDING_TIMEOUT_MS = 3000;

/**
 * The pre-paint cache key (RULE-008). Read by `web/public/theme-boot.js`, which
 * sits outside the module graph and therefore carries the literal - the drift
 * guard in `web/src/theme/__tests__/themeBoot.test.ts` reads this constant out of
 * this file and asserts the script still uses it.
 */
export const BRANDING_CACHE_KEY = 'verisa.branding';

/**
 * The cache schema version. A reader that finds anything else drops the WHOLE
 * object, so bumping this number is how a shape change invalidates every
 * browser's copy without a migration.
 */
export const BRANDING_CACHE_VERSION = 1;

/** What is written to {@link BRANDING_CACHE_KEY}: four keys, nothing else. */
export interface BrandingCacheEntry {
  v: typeof BRANDING_CACHE_VERSION;
  product_name: string;
  favicon_url: string | null;
  primary: string | null;
}

/** The only colour shape that may reach the palette or the cache. */
const BRAND_HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * The two dark surfaces of D-BR-18: the dark theme's default background and its
 * paper. Duplicated from `darkThemeOptions` deliberately - importing the palette
 * here would drag the whole theme module into a provider that mounts above it -
 * and kept honest by the pair of them being the same values
 * `internal/repository/branding_contrast.go` measures against, which the Brand
 * card's readout also uses. The APPLY-time gate itself reads the surfaces off the
 * live options (`applyBrandPalette`); these are for the REPORTED verdict only.
 */
const DARK_SURFACES = ['#121212', '#1e1e1e'] as const;

/** The light surface RULE-013 refuses on: white text on the brand colour. */
const LIGHT_SURFACE = '#ffffff';

/**
 * The only path shape a cached `favicon_url` may have: the same-origin asset
 * stream of CONTRACT-002 (PR #1645). `theme-boot.js` carries the same literal and
 * rejects anything else, including a scheme or a `..` segment; the cache is
 * writable by anything on the origin, so the reader never trusts this side.
 */
const BRANDING_ASSET_PATH_PREFIX = '/api/v1/branding/assets/';

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

/**
 * The provider's read state (RULE-001 as amended by D-BR-17):
 * - `loading` - the first response has not arrived. Bounded at
 *   {@link BRANDING_TIMEOUT_MS}, so this value always resolves. A footer mount
 *   maps it to `AppFooter`'s `'hidden'`: nothing is known yet, so nothing is
 *   claimed - neither a legal notice nor the statement that one is missing.
 * - `ok` - a 200 arrived and its body shape-checked whole.
 * - `unavailable` - the read failed (timeout, abort, non-200, network error or
 *   malformed body). The footer slot renders the fail-VISIBLE line.
 *
 * Exported so a mount site maps all three deliberately; a `switch` over this
 * union is how a new state could never be silently ignored.
 */
export type BrandingState = 'loading' | 'ok' | 'unavailable';

/**
 * One channel's runtime contrast verdict (RULE-007's defence in depth behind the
 * RULE-013 save-time refusal, as amended by D-BR-18). It is REPORTED, never
 * logged: RULE-007 names the console warning as the defect
 * SPEC-FRONTEND-RULE-084 retired.
 */
export interface BrandChannelContrast {
  /** The stored colour, `#rrggbb`, exactly as it was applied. */
  color: string;
  /** White-on-colour against the light surface - the ratio RULE-013 refuses below. */
  lightRatio: number;
  /** The colour against the WORSE of the two dark surfaces (D-BR-18). */
  darkRatio: number;
  /**
   * `false` means a saved colour no longer passes the light-surface gate the
   * gateway refuses on - the RULE-042 degraded state the Brand card renders
   * ("the saved colour no longer passes contrast; re-save"). It can only happen
   * to a value stored before that gate, or after the threshold changed.
   */
  passesLight: boolean;
  /**
   * `false` means the dark theme keeps its BASE colour for this channel
   * (D-BR-18, SPEC-FRONTEND-RULE-084's apply-time gate). Informational: the save
   * succeeded and the light theme - the surface every shipped build paints - uses
   * the colour.
   */
  appliedOnDark: boolean;
}

/** The runtime contrast verdict for the whole brand (RULE-007). */
export interface BrandContrast {
  primary?: BrandChannelContrast;
  secondary?: BrandChannelContrast;
  /**
   * `true` when any channel fails the light-surface gate: the verdict has FIRED
   * and the Brand card renders its RULE-042 degraded state. A dark-surface miss
   * alone does NOT set this - that outcome is ruled and informational (D-BR-18).
   */
  degraded: boolean;
}

/** What `useBranding()` returns. */
export interface BrandingContextValue {
  /** The resolved record, or `DefaultBranding` whenever `state` is not `ok`. */
  branding: Branding;
  /** Where the read has got to. */
  state: BrandingState;
  /**
   * The resolved brand colours as LITERAL `#rrggbb`, or `undefined` when the
   * record carries none (or none that validated). Each SPA root passes this
   * straight to `VerisaThemeProvider`'s `brandPalette` prop (RULE-007); nothing
   * else may put a colour in the MUI palette (SPEC-FRONTEND-RULE-081).
   */
  brandPalette?: BrandPalette;
  /**
   * The runtime contrast verdict, for the Brand card to render. The provider
   * always supplies it - empty (`{degraded: false}`) while loading, on a failed
   * read, and whenever the record carries no colours - and it is OPTIONAL only so
   * that a test double may construct a context value without inventing a verdict.
   * Read it as `brandContrast?.degraded`.
   */
  brandContrast?: BrandContrast;
}

/** No colours read, so no verdict to report. One frozen identity, shared. */
const NO_CONTRAST: BrandContrast = Object.freeze({ degraded: false });

/**
 * The value every consumer sees before the first response: the built-in brand,
 * and an honest `'loading'`. Frozen and shared so the initial render allocates
 * nothing per mount.
 */
const LOADING: BrandingContextValue = Object.freeze({
  branding: DefaultBranding,
  state: 'loading' as const,
  // No brandPalette and no verdict: nothing has been read, so the built-in
  // palette stands and there is nothing to report on.
  brandContrast: NO_CONTRAST,
});

/**
 * The single value used for every failed outcome. One frozen object identity
 * means `setState` on a second failure bails out of the re-render instead of
 * flipping the tree for no change.
 */
const UNAVAILABLE: BrandingContextValue = Object.freeze({
  branding: DefaultBranding,
  state: 'unavailable' as const,
  // The brand fails OPEN (RULE-001): the built-in palette, and no verdict about
  // colours nobody read.
  brandContrast: NO_CONTRAST,
});

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Response parsing - whole record or nothing
// ---------------------------------------------------------------------------

/** A non-null, non-array object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * A `{hu, en}` pair. Both halves must be strings when present; a missing half
 * is an empty string, which renders nothing. Anything else is `null`, which
 * fails the WHOLE record - a half-understood language pair is exactly the
 * "partially applied" value RULE-001 forbids.
 */
function parseLocalizedText(value: unknown): LocalizedText | null {
  if (!isRecord(value)) return null;
  const { hu, en } = value;
  if (hu !== undefined && typeof hu !== 'string') return null;
  if (en !== undefined && typeof en !== 'string') return null;
  return { hu: typeof hu === 'string' ? hu : '', en: typeof en === 'string' ? en : '' };
}

/** `string | null`, with a missing field read as `null` ("unset"). */
function parseNullableString(value: unknown): string | null | undefined {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') return value;
  return undefined; // the caller reads `undefined` as "reject the record"
}

function parseColors(value: unknown): BrandingColors | null {
  if (!isRecord(value)) return null;
  const primary = parseNullableString(value.primary);
  const secondary = parseNullableString(value.secondary);
  if (primary === undefined || secondary === undefined) return null;
  return { primary, secondary };
}

function parseLinks(value: unknown): LegalFooterLinks | null {
  if (!isRecord(value)) return null;
  const privacy = parseLocalizedText(value.privacy);
  const terms = parseLocalizedText(value.terms);
  const accessibility = parseLocalizedText(value.accessibility);
  if (!privacy || !terms || !accessibility) return null;
  return { privacy, terms, accessibility };
}

function parseLegalFooter(value: unknown): LegalFooterValues | null {
  if (!isRecord(value)) return null;
  if (typeof value.enabled !== 'boolean') return null;
  const line = parseLocalizedText(value.line);
  const impressum = parseLocalizedText(value.impressum);
  const links = parseLinks(value.links);
  if (!line || !impressum || !links) return null;
  return { enabled: value.enabled, line, impressum, links };
}

/**
 * Shape-check a 200 body before ANY of it becomes the context value
 * (CONTRACT-001). The check is ALL OR NOTHING: an unexpected body yields `null`
 * and the caller falls back to `DefaultBranding` WHOLE, never field by field,
 * so no consumer can ever read a record that is half server and half built-in.
 *
 * Unknown extra fields are ignored rather than refused: a gateway that adds a
 * field in a later story must not blank the legal footer of an older SPA.
 */
export function parseBranding(body: unknown): Branding | null {
  if (!isRecord(body)) return null;
  if (typeof body.product_name !== 'string' || body.product_name === '') return null;
  const logoURL = parseNullableString(body.logo_url);
  const faviconURL = parseNullableString(body.favicon_url);
  if (logoURL === undefined || faviconURL === undefined) return null;
  const colors = parseColors(body.colors);
  const loginWelcome = parseLocalizedText(body.login_welcome);
  const legalFooter = parseLegalFooter(body.legal_footer);
  if (!colors || !loginWelcome || !legalFooter) return null;
  return {
    product_name: body.product_name,
    logo_url: logoURL,
    favicon_url: faviconURL,
    colors,
    login_welcome: loginWelcome,
    legal_footer: legalFooter,
  };
}

// ---------------------------------------------------------------------------
// The brand half: palette, verdict, and what is applied to the document
// ---------------------------------------------------------------------------

/** A colour the palette and the cache may carry, or `undefined`. */
function brandHex(value: string | null): string | undefined {
  return value !== null && BRAND_HEX.test(value) ? value : undefined;
}

/**
 * The `brandPalette` value for a resolved record: literal hex only, and
 * `undefined` when neither channel is set, so a root can pass it straight through
 * and `VerisaThemeProvider` can skip the application entirely (RULE-007).
 */
export function brandPaletteOf(colors: BrandingColors): BrandPalette | undefined {
  const primary = brandHex(colors.primary);
  const secondary = brandHex(colors.secondary);
  if (!primary && !secondary) return undefined;
  return {
    ...(primary ? { primary } : {}),
    ...(secondary ? { secondary } : {}),
  };
}

/** One channel's two ratios and the two verdicts they decide (D-BR-18). */
function channelContrast(color: string): BrandChannelContrast {
  const lightRatio = getContrastRatio(LIGHT_SURFACE, color);
  // The WORSE of the two dark surfaces: D-BR-18 applies the colour only when it
  // clears BOTH, so the number worth reporting is the one that decides it.
  const darkRatio = Math.min(...DARK_SURFACES.map((surface) => getContrastRatio(color, surface)));
  return {
    color,
    lightRatio,
    darkRatio,
    passesLight: meetsAA(lightRatio),
    appliedOnDark: meetsAA(darkRatio),
  };
}

/**
 * The runtime verdict for a resolved record. Computed once per successful read,
 * not per render, and never logged (RULE-007).
 */
export function brandContrastOf(colors: BrandingColors): BrandContrast {
  const primary = brandHex(colors.primary);
  const secondary = brandHex(colors.secondary);
  if (!primary && !secondary) return NO_CONTRAST;
  const verdicts: BrandContrast = {
    ...(primary ? { primary: channelContrast(primary) } : {}),
    ...(secondary ? { secondary: channelContrast(secondary) } : {}),
    degraded: false,
  };
  return {
    ...verdicts,
    degraded:
      verdicts.primary?.passesLight === false || verdicts.secondary?.passesLight === false,
  };
}

/**
 * The cache entry for a successful read (RULE-008). Four keys, each one already
 * VALIDATED to the reader's rules, so the object `theme-boot.js` finds is
 * valid-by-construction: a value this writer could not vouch for is stored as
 * `null` rather than as something the reader would have to drop the whole object
 * over - and dropping the whole object would lose the title too.
 */
export function brandingCacheEntry(branding: Branding): BrandingCacheEntry {
  const favicon = branding.favicon_url;
  return {
    v: BRANDING_CACHE_VERSION,
    product_name: branding.product_name || DefaultBranding.product_name,
    favicon_url:
      favicon !== null && favicon.startsWith(BRANDING_ASSET_PATH_PREFIX) ? favicon : null,
    primary: brandHex(branding.colors.primary) ?? null,
  };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/** {@link BrandingProvider}'s props. */
export interface BrandingProviderProps {
  children: React.ReactNode;
  /**
   * This SPA's title suffix - `Admin`, `Management` or `Workspace` - which the
   * MOUNT owns and passes as a translated Lingui string (D-BR-08; the strings
   * themselves belong to TASK-PB-BRAND-CARD and the two shell TASKs, which is why
   * this file holds none of them). With a suffix the title is
   * "<product name> - <suffix>" with an ASCII hyphen-minus (RULE-064); without
   * one it is the product name alone, which is also all `theme-boot.js` can paint
   * pre-paint, since the cache carries the name and not the composed title.
   */
  appSuffix?: string;
  /**
   * Write the {@link BRANDING_CACHE_KEY} pre-paint cache (default true). The
   * cache exists only for the SPA entries' pre-paint `theme-boot.js`; a page with
   * no pre-paint reader - the public /verify page - passes false, so an anonymous
   * visitor's device is not written to for nothing (ePrivacy Art. 5(3);
   * TASK-AIMARK-VERIFY-PUBLIC-PAGE).
   */
  persistCache?: boolean;
}

/**
 * Mount ABOVE `VerisaThemeProvider` in an SPA root, and inside `I18nProvider`
 * so consumers can still resolve a locale (UI-004).
 */
export function BrandingProvider({
  children,
  appSuffix,
  persistCache = true,
}: BrandingProviderProps): React.ReactElement {
  const [value, setValue] = useState<BrandingContextValue>(LOADING);

  useEffect(() => {
    const controller = new AbortController();
    // The bound of RULE-001. `abort()` rejects the in-flight fetch, which the
    // catch below turns into 'unavailable' - the same resolution as a 500. This
    // is also what makes 'loading' temporary by construction (D-BR-17): there is
    // no path on which the state never settles.
    const timer = setTimeout(() => controller.abort(), BRANDING_TIMEOUT_MS);
    let cancelled = false;
    // Whether the first read applied a record - the precondition of tier (2).
    let firstReadOk = false;
    // The first read's footer, kept so a logout can put it back (CHG-001B).
    let firstFooter: LegalFooterValues | undefined;

    const read = async (): Promise<void> => {
      try {
        const resp = await fetch(`${API_BASE_URL}${BRANDING_PATH}`, {
          method: 'GET',
          // No Authorization, no tenant header, no `credentials`: the route is
          // public and renders before a principal exists.
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        // Any non-200 - including the contract's 500, and a 401/403 that cannot
        // happen by contract - is 'unavailable'. Never a retry with a token.
        if (!resp.ok) {
          if (!cancelled) setValue(UNAVAILABLE);
          return;
        }
        const parsed = parseBranding(await resp.json());
        if (cancelled) return;
        firstReadOk = parsed !== null;
        firstFooter = parsed?.legal_footer;
        setValue(
          parsed === null
            ? UNAVAILABLE
            : {
                branding: parsed,
                state: 'ok',
                // Derived HERE, once per read, so the palette and the verdict keep
                // one identity for as long as the record does: the theme memo and
                // the CSS-variable effect downstream key on it.
                brandPalette: brandPaletteOf(parsed.colors),
                brandContrast: brandContrastOf(parsed.colors),
              },
        );
      } catch {
        // Network error, abort, timeout, or a body that is not JSON.
        if (!cancelled) setValue(UNAVAILABLE);
      } finally {
        clearTimeout(timer);
      }
    };

    // -----------------------------------------------------------------------
    // RULE-019 tier (2): the ONE footer-only read after authentication
    // -----------------------------------------------------------------------
    //
    // Fires when BOTH are true: the first read settled 'ok', and a token is
    // registered. Either can happen first, so each side calls `onTokenChange`.
    // A first read that FAILED makes no second read: the provider is in its
    // RULE-001 unavailable posture with the built-in brand, and a footer-only
    // patch cannot lift it to 'ok' without the brand half the ruling discards.
    const secondController = new AbortController();
    let secondTimer: ReturnType<typeof setTimeout> | undefined;
    // The once-guard: set before the fetch, never cleared, so a token refresh
    // (which re-registers the getter) or a re-login can never cause a third
    // read. It is the ONLY such guard: the subscription stays open after the
    // read fires, because a logout must still be heard (CHG-001B).
    let secondFired = false;
    // Set when the token goes to null after the second read fired: from then on
    // no tenant footer may be applied in this page load, including a late
    // response of a second read that was still in flight.
    let revoked = false;
    let unsubscribe: (() => void) | undefined;
    let unsubscribeDiscovery: (() => void) | undefined;

    const stopListening = (): void => {
      unsubscribe?.();
      unsubscribe = undefined;
      unsubscribeDiscovery?.();
      unsubscribeDiscovery = undefined;
    };

    const readFooterOnly = async (token: string): Promise<void> => {
      // The same RULE-001 bound as the first read.
      secondTimer = setTimeout(() => secondController.abort(), BRANDING_TIMEOUT_MS);
      try {
        const resp = await fetch(`${API_BASE_URL}${BRANDING_PATH}`, {
          method: 'GET',
          // LANE (A)4 of SPEC-FRONTEND-RULE-095(6): a raw fetch that carries the
          // user bearer also carries the act-as header, so inside a binding the
          // footer is decided for the TARGET, not the operator - the page shows the
          // viewed tenant's legal notice, which is what "view as" means.
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, ...actAsHeaders() },
          signal: secondController.signal,
        });
        if (!resp.ok) return;
        // Whole-record shape check (CONTRACT-001) before the footer half is
        // trusted; everything but `legal_footer` is then thrown away.
        const parsed = parseBranding(await resp.json());
        if (cancelled || revoked || parsed === null) return;
        const footer = parsed.legal_footer;
        setValue((prev) =>
          prev.state !== 'ok'
            ? prev
            : {
                // `prev` keeps the FIRST read's brand, palette and verdict
                // identities, so no consumer or theme memo sees a brand change.
                ...prev,
                branding: { ...prev.branding, legal_footer: footer },
              },
        );
      } catch {
        // Network error, abort, timeout or non-JSON: the first read's footer
        // stands. NOT 'unavailable' - the legal text on screen is still true.
      } finally {
        clearTimeout(secondTimer);
      }
    };

    /** A logout after the second read: the first read's footer, from memory. */
    const restoreFirstFooter = (): void => {
      revoked = true;
      secondController.abort();
      const footer = firstFooter;
      if (!footer) return;
      setValue((prev) =>
        prev.state !== 'ok' || prev.branding.legal_footer === footer
          ? prev
          : { ...prev, branding: { ...prev.branding, legal_footer: footer } },
      );
    };

    const onTokenChange = (): void => {
      if (cancelled || !firstReadOk) return;
      const token = getAccessToken();
      if (!token) {
        // Before the second read there is nothing to undo; after it, a null
        // token is a logout and the installation footer comes back.
        if (secondFired) restoreFirstFooter();
        return;
      }
      if (secondFired) return;
      // THE RACE (TASK-IMP-BRANDING-UNDER-BINDING CHG-004): on a reload the
      // binding is re-discovered AFTER the ordinary bootstrap, so a read on the
      // first token would leave before the act-as getter is armed and be answered
      // for the OPERATOR - and the once-guard would forbid the re-read. So the
      // read also waits for discovery to SETTLE (no binding, or a binding armed).
      // A discovery that ended in error makes no read: a read whose identity is
      // unknown must not pick a tenant, and the first read's footer stands.
      if (getImpersonationDiscoveryState() !== 'settled') return;
      secondFired = true;
      void readFooterOnly(token);
    };

    // Open until unmount (CHG-001B), not until the second read fires.
    unsubscribe = subscribeAccessToken(onTokenChange);
    unsubscribeDiscovery = subscribeImpersonationDiscovery(onTokenChange);

    void read().then(() => {
      if (firstReadOk) {
        // Covers a getter registered BEFORE this effect ran (VITE_AUTH_DISABLED
        // renders with a dev token, and AuthProvider's effect runs first), as
        // well as a login that landed while the first read was in flight.
        onTokenChange();
      } else {
        stopListening();
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
      stopListening();
      clearTimeout(secondTimer);
      secondController.abort();
    };
  }, []);

  // -------------------------------------------------------------------------
  // The brand half of RULE-007 / RULE-008: title, favicon, pre-paint cache
  // -------------------------------------------------------------------------
  //
  // GUARDED ON 'ok', AND THAT GUARD IS THE POINT. On 'loading' and 'unavailable'
  // this effect writes nothing, so a failed read leaves whatever painted - the
  // built-in brand on a first visit, the CACHED brand on a repeat one. The
  // alternative (apply DefaultBranding whenever the record is not 'ok') is the
  // vendor-brand flash RULE-008 exists to prevent, dressed as fail-open.
  //
  // Silent on every path (RULE-001): no console line, no rendered error. Only the
  // legal footer fails visible, and it fails through `state` (D-BR-15).
  useEffect(() => {
    if (value.state !== 'ok') return;
    const { branding } = value;

    // An empty product_name cannot reach here - EffectiveBranding guarantees one
    // server-side (RULE-016) and parseBranding refuses an empty string - but the
    // built-in name is still preferred over writing an empty title.
    const productName = branding.product_name || DefaultBranding.product_name;
    // ASCII hyphen-minus, not an en dash (RULE-064, DASH-GATE).
    document.title = appSuffix ? `${productName} - ${appSuffix}` : productName;

    // MUTATE the existing element (RULE-008): all three HTML entries ship a
    // <link rel="icon">, and appending a second one leaves the browser choosing
    // between two icons. No element, nothing to do - the provider does not invent
    // a favicon the entry did not declare.
    if (branding.favicon_url) {
      const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (link) link.setAttribute('href', branding.favicon_url);
    }

    if (!persistCache) return;
    try {
      window.localStorage.setItem(
        BRANDING_CACHE_KEY,
        JSON.stringify(brandingCacheEntry(branding)),
      );
    } catch {
      // A private window, a disabled store or a full quota. The cache is an
      // optimisation for the NEXT visit; losing it costs one unbranded first
      // paint and nothing else, so it is never surfaced.
    }
  }, [value, appSuffix, persistCache]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Read the branding context. Throws outside a `BrandingProvider` (the
 * `usePermissionContext` precedent, `web/src/store/PermissionContext.tsx`): a
 * surface that silently fell back to the built-in brand because someone forgot
 * the provider would also silently drop the legal footer.
 */
export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return ctx;
}
